/**
 * Smart Token Refund System
 * Automatically refunds tokens when AI requests fail
 */

import { User } from '../types';

export interface RefundRecord {
    id: string;
    userId: string;
    tokensRefunded: number;
    reason: string;
    timestamp: number;
    originalRequest: string;
}

/**
 * Refund tokens to user when a request fails
 */
export async function refundTokens(
    user: User,
    tokensToRefund: number,
    reason: string,
    originalRequest: string
): Promise<User> {
    console.log(`🔄 Token Refund Initiated: ${tokensToRefund} tokens for user ${user.username}`);
    console.log(`Reason: ${reason}`);

    // Calculate new token usage (subtract refunded tokens)
    const newTokenUsage = Math.max(0, (user.dailyTokenUsage || 0) - tokensToRefund);

    // Create refund record
    const refundRecord: RefundRecord = {
        id: `refund_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        userId: user.id,
        tokensRefunded: tokensToRefund,
        reason,
        timestamp: Date.now(),
        originalRequest: originalRequest.substring(0, 200) // Store first 200 chars
    };

    // Save refund record to localStorage
    try {
        const refundHistory = JSON.parse(localStorage.getItem(`refund_history_${user.id}`) || '[]');
        refundHistory.push(refundRecord);
        
        // Keep only last 50 refunds
        if (refundHistory.length > 50) {
            refundHistory.shift();
        }
        
        localStorage.setItem(`refund_history_${user.id}`, JSON.stringify(refundHistory));
    } catch (error) {
        console.error('Failed to save refund record:', error);
    }

    // Update user object
    const updatedUser: User = {
        ...user,
        dailyTokenUsage: newTokenUsage
    };

    // Update localStorage
    localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));
    
    const allUsers = JSON.parse(localStorage.getItem('ledger_users') || '[]');
    const updatedUsers = allUsers.map((u: User) => u.id === user.id ? updatedUser : u);
    localStorage.setItem('ledger_users', JSON.stringify(updatedUsers));

    // Firebase update will be handled by the realtime sync in App.tsx
    // We just update localStorage and let the sync mechanism handle Firebase

    console.log(`✅ Refund Complete: ${tokensToRefund} tokens refunded. New usage: ${newTokenUsage}`);

    return updatedUser;
}

/**
 * Get refund history for a user
 */
export function getRefundHistory(userId: string): RefundRecord[] {
    try {
        return JSON.parse(localStorage.getItem(`refund_history_${userId}`) || '[]');
    } catch (error) {
        console.error('Failed to load refund history:', error);
        return [];
    }
}

/**
 * Calculate total refunded tokens for a user
 */
export function getTotalRefundedTokens(userId: string): number {
    const history = getRefundHistory(userId);
    return history.reduce((total, record) => total + record.tokensRefunded, 0);
}

/**
 * Determine if a request should be refunded based on error type
 */
export function shouldRefund(error: any): { shouldRefund: boolean; reason: string } {
    const errorMessage = error?.message || error?.toString() || 'Unknown error';

    // Network errors
    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        return { shouldRefund: true, reason: 'Network connection error' };
    }

    // API errors
    if (errorMessage.includes('API') || errorMessage.includes('quota')) {
        return { shouldRefund: true, reason: 'API service error' };
    }

    // Timeout errors
    if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
        return { shouldRefund: true, reason: 'Request timeout' };
    }

    // Server errors (5xx)
    if (errorMessage.includes('500') || errorMessage.includes('502') || errorMessage.includes('503')) {
        return { shouldRefund: true, reason: 'Server error' };
    }

    // Rate limiting
    if (errorMessage.includes('rate limit') || errorMessage.includes('429')) {
        return { shouldRefund: true, reason: 'Rate limit exceeded' };
    }

    // Aborted by user - do NOT refund (user intentionally stopped)
    if (errorMessage.includes('abort') || errorMessage.includes('cancelled')) {
        return { shouldRefund: false, reason: 'Request cancelled by user' };
    }

    // Invalid input - do NOT refund (user error)
    if (errorMessage.includes('invalid') || errorMessage.includes('400')) {
        return { shouldRefund: false, reason: 'Invalid request' };
    }

    // Default: refund for unknown errors to be user-friendly
    return { shouldRefund: true, reason: 'Unexpected error occurred' };
}

// ==================== SMART RESPONSE QUALITY DETECTION ====================

export interface ResponseQualityCheck {
    isComplete: boolean;
    hasBrokenTables: boolean;
    isCutOff: boolean;
    qualityScore: number; // 0-100
    issues: string[];
    suggestedRefundPercent: number; // 0-100
}

/**
 * Analyze AI response quality and detect issues
 */
export function analyzeResponseQuality(response: string): ResponseQualityCheck {
    const issues: string[] = [];
    let qualityScore = 100;
    let hasBrokenTables = false;
    
    // Check for cut-off indicators
    const cutOffPatterns = [
        /\.{3,}$/,           // Ends with ...
        /\.\.\.\s*$/,        // Ends with ... with whitespace
        /[^.!?]\s*$/,        // Doesn't end with proper punctuation
        /\bI'll\s*$/i,       // Cut off mid-sentence "I'll"
        /\band\s*$/i,        // Cut off mid-sentence "and"
        /\bthe\s*$/i,        // Cut off mid-sentence "the"
        /\bto\s*$/i,         // Cut off mid-sentence "to"
        /\bfor\s*$/i,        // Cut off mid-sentence "for"
        /\bin\s*$/i,         // Cut off mid-sentence "in"
        /\bwith\s*$/i,       // Cut off mid-sentence "with"
        /\ba\s*$/i,          // Cut off mid-sentence "a"
        /\bis\s*$/i,         // Cut off mid-sentence "is"
        /\bare\s*$/i,        // Cut off mid-sentence "are"
        /\bthat\s*$/i,       // Cut off mid-sentence "that"
        /\bwhich\s*$/i,      // Cut off mid-sentence "which"
        /[,;:]\s*$/,         // Ends with comma, semicolon, or colon
    ];
    
    const isCutOff = cutOffPatterns.some(pattern => pattern.test(response.trim()));
    if (isCutOff) {
        issues.push('Response appears to be cut off');
        qualityScore -= 30;
    }
    
    // Check for tables (STRICTER v6.0)
    const tableMatches = response.match(/\|[^\n]+\|/g);
    if (tableMatches && tableMatches.length > 0) {
        const tableRows = tableMatches;
        
        // Check if all rows have same number of pipes (columns)
        const pipeCounts = tableRows.map(row => (row.match(/\|/g) || []).length);
        const hasMismatchedColumns = new Set(pipeCounts).size > 1;
        if (hasMismatchedColumns) {
            issues.push('Table has inconsistent columns');
            qualityScore -= 30; // Increased penalty
        }
        
        // Check for separator row
        const hasSeparator = tableRows.some(row => /^\|[\s\-:]+\|$/m.test(row));
        if (tableRows.length > 1 && !hasSeparator) {
            issues.push('Table missing separator row');
            qualityScore -= 25; // Increased penalty
        }
        
        // Check for incomplete table (odd number of pipes in a row)
        const incompleteRow = tableRows.some(row => {
            const pipes = (row.match(/\|/g) || []).length;
            return pipes < 3; // Minimum 2 columns = 3 pipes
        });
        if (incompleteRow) {
            issues.push('Table has incomplete rows');
            qualityScore -= 25; // Increased penalty
        }
        
        // NEW: Check for cells that are too long
        const cellsMatch = response.match(/\|([^\|\n]+)\|/g);
        if (cellsMatch) {
            const hasLongCells = cellsMatch.some(cell => {
                const content = cell.replace(/\|/g, '').trim();
                return content.length > 40; // Cells should be short
            });
            if (hasLongCells) {
                issues.push('Table cells too long (should use bullets)');
                qualityScore -= 20;
            }
        }
        
        // NEW: Check for tables without blank lines
        const tableBlocks = response.split('\n\n').filter(block => block.includes('|'));
        if (tableBlocks.length > 0) {
            const hasIsolatedTables = tableBlocks.every(block => {
                const lines = block.split('\n');
                return lines.filter(l => l.includes('|')).length === lines.length;
            });
            if (!hasIsolatedTables) {
                issues.push('Table not properly isolated with blank lines');
                qualityScore -= 15;
            }
        }
        
        // NEW: Check for too many tables (should use bullets instead)
        const tableBlockCount = (response.match(/\n\n[^\n]*\|/g) || []).length;
        if (tableBlockCount > 2) {
            issues.push('Too many tables (should use bullet points)');
            qualityScore -= 15;
        }
    }
    
    // Check for incomplete numbered lists
    const listMatches = response.match(/^\d+\.\s/gm) || [];
    if (listMatches.length > 0) {
        const numbers = listMatches.map(m => parseInt(m));
        const expectedSequence = numbers.every((n, i) => i === 0 || n === numbers[i-1] + 1);
        if (!expectedSequence && numbers.length > 2) {
            issues.push('Numbered list has gaps');
            qualityScore -= 10;
        }
    }
    
    // Check for very short responses to complex questions
    const wordCount = response.split(/\s+/).length;
    if (wordCount < 50) {
        issues.push('Response may be too brief');
        qualityScore -= 10;
    }
    
    // Calculate suggested refund
    let suggestedRefundPercent = 0;
    if (qualityScore < 50) {
        suggestedRefundPercent = 50; // Major issues: 50% refund
    } else if (qualityScore < 70) {
        suggestedRefundPercent = 30; // Moderate issues: 30% refund
    } else if (qualityScore < 90) {
        suggestedRefundPercent = 15; // Minor issues: 15% refund
    }
    
    return {
        isComplete: !isCutOff && qualityScore >= 70,
        hasBrokenTables,
        isCutOff,
        qualityScore: Math.max(0, qualityScore),
        issues,
        suggestedRefundPercent
    };
}

/**
 * Calculate smart refund amount based on response quality
 */
export function calculateSmartRefund(
    tokensCharged: number,
    qualityCheck: ResponseQualityCheck
): { refundAmount: number; reason: string } {
    if (qualityCheck.qualityScore >= 90) {
        return { refundAmount: 0, reason: 'Response quality acceptable' };
    }
    
    const refundAmount = Math.ceil(tokensCharged * (qualityCheck.suggestedRefundPercent / 100));
    const reason = qualityCheck.issues.join('; ') || 'Quality issues detected';
    
    return { refundAmount, reason };
}

/**
 * Process smart refund for a response
 */
export async function processSmartRefund(
    user: User,
    response: string,
    tokensCharged: number,
    originalRequest: string
): Promise<{ user: User; refunded: boolean; amount: number; reason: string }> {
    const qualityCheck = analyzeResponseQuality(response);
    
    console.log(`[SmartRefund] Quality Score: ${qualityCheck.qualityScore}%`);
    console.log(`[SmartRefund] Issues:`, qualityCheck.issues);
    
    if (qualityCheck.qualityScore >= 90) {
        return { user, refunded: false, amount: 0, reason: 'No refund needed' };
    }
    
    const { refundAmount, reason } = calculateSmartRefund(tokensCharged, qualityCheck);
    
    if (refundAmount > 0) {
        console.log(`[SmartRefund] Issuing refund: ${refundAmount} tokens (${qualityCheck.suggestedRefundPercent}%)`);
        const updatedUser = await refundTokens(user, refundAmount, `Smart Refund: ${reason}`, originalRequest);
        return { user: updatedUser, refunded: true, amount: refundAmount, reason };
    }
    
    return { user, refunded: false, amount: 0, reason: 'No refund needed' };
}